import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';

const API = process.env.REACT_APP_BACKEND_URL;

function MyAccount() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setUser(userData);
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/customer-360/my-dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('active') || s.includes('live') || s.includes('paid') || s.includes('completed')) 
      return 'bg-green-100 text-green-800';
    if (s.includes('pending') || s.includes('progress') || s.includes('setup')) 
      return 'bg-yellow-100 text-yellow-800';
    if (s.includes('inactive') || s.includes('cancelled') || s.includes('rejected')) 
      return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'payments', label: 'Payments', icon: '💳' },
    { id: 'timeline', label: 'Journey', icon: '📈' },
    { id: 'documents', label: 'Documents', icon: '📄' }
  ];

  if (loading) {
    return (
      <Layout user={user} onLogout={handleLogout}>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="text-gray-500">Loading your dashboard...</div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!dashboardData || !dashboardData.customer_type) {
    return (
      <Layout user={user} onLogout={handleLogout}>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold mb-2" style={{ color: '#2b3380' }}>My Account</h1>
            <p className="text-gray-600">View your account details</p>
          </div>
          
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold mb-2" style={{ color: '#2b3380' }}>No Customer Profile Found</h2>
            <p className="text-gray-600 mb-6">
              Your account is not linked to any customer profile. If you're a franchisee or partner, 
              please contact support to link your account.
            </p>
            <div className="bg-gray-50 rounded-lg p-4 text-left max-w-md mx-auto">
              <h3 className="font-medium text-gray-900 mb-2">Your Account Details:</h3>
              <div className="space-y-2 text-sm text-gray-600">
                <p><strong>Name:</strong> {dashboardData?.profile?.name || user?.name || '-'}</p>
                <p><strong>Email:</strong> {dashboardData?.profile?.email || user?.email || '-'}</p>
                <p><strong>Phone:</strong> {dashboardData?.profile?.phone || user?.mobile_number || '-'}</p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const isLead = dashboardData.customer_type === 'lead';
  const isFranchisee = dashboardData.customer_type === 'franchisee';

  return (
    <Layout user={user} onLogout={handleLogout}>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#2b3380' }}>My Account</h1>
          <p className="text-gray-600">
            {isFranchisee ? 'Track your franchise journey and payments' : 
             isLead ? 'Track your application progress' : 
             'View your account details'}
          </p>
        </div>

        {/* Profile Card */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            {/* Profile Info */}
            <div className="flex items-center gap-4 flex-1">
              <div className="w-16 h-16 rounded-lg flex items-center justify-center text-white text-2xl font-bold"
                   style={{ backgroundColor: '#2b3380' }}>
                {dashboardData.profile?.name?.charAt(0) || dashboardData.profile?.store_name?.charAt(0) || 'U'}
              </div>
              <div>
                <h2 className="text-xl font-bold" style={{ color: '#2b3380' }}>
                  {dashboardData.profile?.name || dashboardData.profile?.store_name}
                </h2>
                {isFranchisee && dashboardData.profile?.store_code && (
                  <p className="text-blue-600 font-medium">{dashboardData.profile.store_code}</p>
                )}
                {isLead && dashboardData.profile?.lead_number && (
                  <p className="text-blue-600 font-medium">{dashboardData.profile.lead_number}</p>
                )}
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(dashboardData.profile?.status)}`}>
                    {dashboardData.profile?.status}
                  </span>
                  {dashboardData.profile?.city && (
                    <span>📍 {dashboardData.profile.city}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-700">{formatCurrency(dashboardData.stats?.total_investment)}</div>
                <div className="text-xs text-blue-600">Total Investment</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-700">{formatCurrency(dashboardData.stats?.amount_paid)}</div>
                <div className="text-xs text-green-600">Amount Paid</div>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-orange-700">{formatCurrency(dashboardData.stats?.balance_due)}</div>
                <div className="text-xs text-orange-600">Balance Due</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-purple-700">{dashboardData.stats?.payment_progress || 0}%</div>
                <div className="text-xs text-purple-600">Progress</div>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">Payment Progress</span>
              <span className="font-medium" style={{ color: '#2b3380' }}>{dashboardData.stats?.payment_progress || 0}%</span>
            </div>
            <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-500"
                style={{ 
                  width: `${dashboardData.stats?.payment_progress || 0}%`,
                  backgroundColor: '#2b3380'
                }}
              ></div>
            </div>
          </div>

          {/* Journey Stages */}
          {dashboardData.journey_stage && (
            <div className="mt-6 pt-6 border-t">
              <h3 className="text-sm font-medium text-gray-700 mb-4">Your Journey</h3>
              <div className="flex items-center justify-between overflow-x-auto pb-2">
                {dashboardData.journey_stage.stages?.map((stage, idx) => (
                  <React.Fragment key={stage.id}>
                    <div className={`flex flex-col items-center min-w-[70px] ${stage.current ? 'scale-110' : ''}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg transition-all ${
                        stage.completed ? 'bg-green-500 text-white' :
                        stage.current ? 'text-white ring-4 ring-blue-200' :
                        'bg-gray-200 text-gray-500'
                      }`}
                      style={stage.current ? { backgroundColor: '#2b3380' } : {}}>
                        {stage.completed ? '✓' : stage.icon}
                      </div>
                      <span className={`text-xs mt-2 font-medium text-center ${
                        stage.current ? 'text-blue-800' : stage.completed ? 'text-green-600' : 'text-gray-500'
                      }`}>
                        {stage.name}
                      </span>
                    </div>
                    {idx < dashboardData.journey_stage.stages.length - 1 && (
                      <div className={`flex-1 h-1 mx-2 rounded min-w-[20px] ${
                        stage.completed ? 'bg-green-500' : 'bg-gray-200'
                      }`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b">
            <div className="flex space-x-4 p-4 overflow-x-auto">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 whitespace-nowrap ${
                    activeTab === tab.id ? 'text-white' : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                  style={activeTab === tab.id ? { backgroundColor: '#2b3380' } : {}}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Pending Payments */}
                <div>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#2b3380' }}>
                    ⚠️ Pending Payments
                  </h3>
                  {dashboardData.pending_payments && dashboardData.pending_payments.length > 0 ? (
                    <div className="space-y-3">
                      {dashboardData.pending_payments.map((payment, idx) => (
                        <div key={idx} className="border border-orange-200 bg-orange-50 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="font-medium text-gray-900">{payment.milestone || payment.description}</div>
                              <div className="text-sm text-gray-600 mt-1">Due: {formatDate(payment.due_date)}</div>
                            </div>
                            <div className="text-right">
                              <div className="text-xl font-bold text-orange-600">{formatCurrency(payment.amount)}</div>
                              <span className={`px-2 py-1 text-xs rounded-full font-medium ${getStatusBadge(payment.status)}`}>
                                {payment.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-green-50 rounded-lg p-6 text-center">
                      <div className="text-4xl mb-2">✅</div>
                      <p className="text-green-700 font-medium">No pending payments!</p>
                    </div>
                  )}
                </div>

                {/* Recent Payments */}
                <div>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: '#2b3380' }}>
                    ✅ Recent Payments
                  </h3>
                  {dashboardData.recent_payments && dashboardData.recent_payments.length > 0 ? (
                    <div className="space-y-3">
                      {dashboardData.recent_payments.map((payment, idx) => (
                        <div key={idx} className="border border-green-200 bg-green-50 rounded-lg p-4">
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="font-medium text-gray-900">{payment.reference_number || 'Payment'}</div>
                              <div className="text-sm text-gray-600">{payment.payment_mode} • {formatDate(payment.payment_date || payment.created_at)}</div>
                            </div>
                            <div className="text-xl font-bold text-green-600">{formatCurrency(payment.amount)}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-6 text-center">
                      <div className="text-4xl mb-2">⏳</div>
                      <p className="text-gray-500">No payments recorded yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Payments Tab */}
            {activeTab === 'payments' && (
              <div className="space-y-6">
                {/* All Pending */}
                {dashboardData.pending_payments && dashboardData.pending_payments.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4" style={{ color: '#2b3380' }}>Pending Payments</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Milestone</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Amount</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Due Date</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {dashboardData.pending_payments.map((payment, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium">{payment.milestone || payment.description}</td>
                              <td className="px-4 py-3 font-semibold text-orange-600">{formatCurrency(payment.amount)}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{formatDate(payment.due_date)}</td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 text-xs rounded-full font-medium ${getStatusBadge(payment.status)}`}>
                                  {payment.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* All Completed */}
                {dashboardData.recent_payments && dashboardData.recent_payments.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold mb-4" style={{ color: '#2b3380' }}>Payment History</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Reference</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Amount</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Mode</th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {dashboardData.recent_payments.map((payment, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 font-medium">{payment.reference_number || payment.id}</td>
                              <td className="px-4 py-3 font-semibold text-green-600">{formatCurrency(payment.amount)}</td>
                              <td className="px-4 py-3 text-sm">{payment.payment_mode}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{formatDate(payment.payment_date || payment.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {(!dashboardData.pending_payments?.length && !dashboardData.recent_payments?.length) && (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">💳</div>
                    <p className="text-gray-500">No payment records found</p>
                  </div>
                )}
              </div>
            )}

            {/* Timeline Tab */}
            {activeTab === 'timeline' && (
              <div className="max-h-[500px] overflow-y-auto">
                {dashboardData.timeline && dashboardData.timeline.length > 0 ? (
                  <div className="relative pl-8">
                    <div className="absolute left-3 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                    <div className="space-y-6">
                      {dashboardData.timeline.map((event, idx) => (
                        <div key={idx} className="relative">
                          <div className={`absolute -left-5 w-6 h-6 rounded-full flex items-center justify-center text-sm ${
                            event.color === 'green' ? 'bg-green-500 text-white' :
                            event.color === 'orange' ? 'bg-orange-500 text-white' :
                            event.color === 'purple' ? 'bg-purple-500 text-white' :
                            'bg-blue-500 text-white'
                          }`}>
                            {event.icon}
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4 ml-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <div className="font-semibold text-gray-900">{event.title}</div>
                                <div className="text-sm text-gray-600 mt-1">{event.description}</div>
                              </div>
                              <div className="text-xs text-gray-500">{formatDate(event.timestamp)}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">📈</div>
                    <p className="text-gray-500">No journey events recorded yet</p>
                  </div>
                )}
              </div>
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
              <div>
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Documents related to your franchise agreement, payments, and store setup will appear here once available.
                  </p>
                </div>
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📄</div>
                  <p className="text-gray-500">No documents available yet</p>
                  <p className="text-sm text-gray-400 mt-2">Documents will be uploaded by your account manager</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-6 rounded-lg p-6 text-white" style={{ backgroundColor: '#2b3380' }}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold">Need Help?</h3>
              <p className="text-blue-100 text-sm mt-1">Contact your account manager for any queries regarding your franchise journey</p>
            </div>
            <div className="flex gap-3">
              <a href="tel:+919876543210" className="px-4 py-2 bg-white rounded-lg font-medium text-sm hover:bg-gray-100 transition-colors flex items-center gap-2"
                 style={{ color: '#2b3380' }}>
                📞 Call Support
              </a>
              <a href="mailto:support@zobox.in" className="px-4 py-2 bg-blue-800 text-white rounded-lg font-medium text-sm hover:bg-blue-700 transition-colors flex items-center gap-2">
                ✉️ Email
              </a>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default MyAccount;
